import { createSolidStreamingMarkdown, SolidStreamingMarkdown } from "@storytell/markdown";
import type { StreamingBlock } from "@storytell/streaming";
import { getBlockContent, subscribeToBlockDeltas, subscribeToBlockStop } from "@storytell/streaming";
import { StButton } from "@storytell/ui/buttons";
import { TbRefresh } from "solid-icons/tb";
import { onCleanup, onMount, Show } from "solid-js";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { LogoNoText } from "../ui";

interface InterstitialUserFacingRendererProps {
  block: StreamingBlock;
  /**
   * Pull the card left into the message gutter so the logo aligns with the
   * avatar column (bluesky). Hosts without that gutter (chief) pass false.
   */
  gutterOffset?: boolean;
}

/**
 * Renders user-facing interstitial blocks - messages that are meant for
 * direct user consumption, like explanations, clarifications, or user-friendly status updates.
 * Uses streaming markdown to properly render text content with delta updates.
 */
export const InterstitialUserFacingRenderer = (props: InterstitialUserFacingRendererProps) => {
  const rt = useChatRuntime();
  const markdownStreamer = createSolidStreamingMarkdown();
  const showRetry = () => props.block.activity === "error" && Boolean(rt.actions.retryMessage);

  const hasContent = () => {
    if (props.block.finalContent?.type === "text" && props.block.finalContent.text) {
      return true;
    }
    return !!markdownStreamer.doc;
  };

  onMount(() => {
    if (props.block.isComplete) {
      if (props.block.finalContent?.type === "text" && props.block.finalContent.text) {
        markdownStreamer.append(props.block.finalContent.text);
      } else {
        const content = getBlockContent(props.block.blockId);
        if (content) {
          markdownStreamer.append(content);
        }
      }
      return;
    }

    const unsubscribeDelta = subscribeToBlockDeltas(
      props.block.blockId,
      (delta: string) => {
        markdownStreamer.append(delta);
      },
      { replay: true }
    );

    const unsubscribeStop = subscribeToBlockStop(props.block.blockId, () => {
      // Block completed
    });

    onCleanup(() => {
      unsubscribeDelta();
      unsubscribeStop();
    });
  });

  const handleRetry = () => {
    const msg = rt.store.message(props.block.messageId);
    if (!msg?.prompt?.text) return;
    rt.actions.trackFeature?.({ feature: "chat.retry", via: "button" });
    rt.actions.retryMessage?.({ promptText: msg.prompt.text, richText: msg.prompt.richText });
  };

  return (
    <Show when={hasContent()}>
      <div
        data-renderer="interstitial-user-facing-renderer"
        class="relative rounded-2xl bg-canvas px-4 py-6"
        classList={{ "left-[-3.7rem]": props.gutterOffset !== false }}
      >
        <div class="flex items-start gap-4">
          <div class="shrink-0">
            <div class="flex size-6 items-center justify-center rounded-full">
              <LogoNoText />
            </div>
          </div>

          <div class="min-w-0 flex-1">
            <Show when={props.block.title}>
              <div class="mb-2 text-base font-medium text-primary-text">{props.block.title}</div>
            </Show>
            <Show when={hasContent() && markdownStreamer.doc}>
              <div class="max-w-none text-base text-primary-text">
                <SolidStreamingMarkdown
                  doc={markdownStreamer.doc}
                  options={{
                    classes: {
                      document: "wrap-break-word whitespace-break-spaces [&>p:last-child]:mb-0",
                      heading: (node) => {
                        switch (node.level) {
                          case 1:
                            return "font-medium text-base mb-2 mt-3";
                          case 2:
                            return "font-medium text-sm mb-2 mt-2";
                          case 3:
                            return "font-medium text-sm mb-1 mt-2";
                          default:
                            return "font-medium text-sm mb-1 mt-1";
                        }
                      },
                      paragraph: "mb-2",
                      code: "bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded text-xs",
                      codeBlock: "bg-blue-100 dark:bg-blue-900 p-3 rounded-lg text-xs font-mono overflow-x-auto mb-3",
                      list: "mb-2",
                      item: (node) => {
                        const isTask = (node as { listData?: { task?: boolean } }).listData?.task;
                        return isTask ? "flex items-center gap-2 mb-1 [&>p]:mb-0" : "mb-1";
                      },
                      strong: "font-semibold",
                      emph: "italic",
                      link: "text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-200",
                      blockQuote:
                        "border-l-2 border-blue-300 dark:border-blue-700 pl-3 py-1 mb-3 text-blue-700 dark:text-blue-100 italic",
                    },
                  }}
                />
              </div>
            </Show>

            <Show when={!props.block.isComplete}>
              <div class="mt-3 flex items-center gap-2">
                <div class="size-2 animate-pulse rounded-full bg-blue-500" />
                <span class="text-xs text-blue-600 dark:text-blue-300">Composing...</span>
              </div>
            </Show>
            <Show when={showRetry()}>
              <div class="mt-4">
                <StButton class="px-3 py-1.5" icon={TbRefresh} onClick={handleRetry}>
                  Retry Prompt
                </StButton>
              </div>
            </Show>
          </div>
        </div>
      </div>
    </Show>
  );
};
